import {onMounted, onScopeDispose} from 'vue';
import type {Emitter} from '@malt/vue-event-bus/create-event-bus';

interface UseEventBusOptions {
    /**
     * Register the listener immediately on create instead of mount
     *
     * @default false
     */
    immediate?: boolean;
}

/**
 * Register a event bus listener than will disconnect itself on destroy!
 *
 * ```ts
 * useEventBus(eventBus, 'myEvent', (value) => {});
 * ```
 */
export function useEventBus<T extends Record<string, unknown>, E extends keyof T>(
    eventBus: Emitter<T>,
    event: E,
    handler: (event: T[E]) => void,
    options?: UseEventBusOptions,
) {
    const {immediate = false} = options ?? {};

    if (immediate) {
        eventBus.on(event, handler);
    } else {
        onMounted(() => {
            eventBus.on(event, handler);
        });
    }

    onScopeDispose(() => {
        eventBus.off(event, handler);
    });
}
